import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle, Key, Trash2, Check, ExternalLink, Loader2, Brain, Github, HelpCircle } from 'lucide-react';
import { API_KEY_TYPES, getApiKeyStatus, setApiKey, deleteApiKey, testApiKey } from '../../services/apiKeysApi';
import { useAuth } from './useAuth';
import { resetPostImportTour } from '../onboarding';
import { TextSizeControl } from '../ui/TextSizeControl';

export const AccountSettings = ({ onNavigateToAIConfig }) => {
  const { user, connectGitHub, getLinkedProviders, isSuperadmin } = useAuth();
  const [message, setMessage] = useState(null);

  // API Keys state
  const [apiKeys, setApiKeys] = useState({});
  const [keyInputs, setKeyInputs] = useState({});
  const [keyVisibility, setKeyVisibility] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [testingKey, setTestingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [keyMessage, setKeyMessage] = useState(null);

  // Load API key status on mount — only for superadmins
  useEffect(() => {
    if (!isSuperadmin()) return;
    const loadApiKeyStatus = async () => {
      const result = await getApiKeyStatus();
      if (result.keys) {
        setApiKeys(result.keys);
      }
    };
    loadApiKeyStatus();
  }, [isSuperadmin]);

  const handleSaveApiKey = async (keyType) => {
    const keyValue = keyInputs[keyType];
    if (!keyValue?.trim()) return;

    setSavingKey(keyType);
    setKeyMessage(null);

    const result = await setApiKey(keyType, keyValue.trim());

    if (result.success) {
      setKeyMessage({ type: 'success', text: `${API_KEY_TYPES[keyType.toUpperCase()]?.name || keyType} key saved successfully` });
      setKeyInputs(prev => ({ ...prev, [keyType]: '' }));
      const status = await getApiKeyStatus();
      if (status.keys) setApiKeys(status.keys);
    } else {
      setKeyMessage({ type: 'error', text: result.error || 'Failed to save API key' });
    }

    setSavingKey(null);
  };

  const handleTestApiKey = async (keyType) => {
    setTestingKey(keyType);
    setKeyMessage(null);

    const result = await testApiKey(keyType);

    if (result.success) {
      setKeyMessage({ type: 'success', text: `${API_KEY_TYPES[keyType.toUpperCase()]?.name || keyType} key is valid` });
    } else {
      setKeyMessage({ type: 'error', text: result.error || 'API key test failed' });
    }

    setTestingKey(null);
  };

  const handleDeleteApiKey = async (keyType) => {
    setDeletingKey(keyType);
    setKeyMessage(null);

    const result = await deleteApiKey(keyType);

    if (result.success) {
      setKeyMessage({ type: 'success', text: `${API_KEY_TYPES[keyType.toUpperCase()]?.name || keyType} key deleted` });
      const status = await getApiKeyStatus();
      if (status.keys) setApiKeys(status.keys);
    } else {
      setKeyMessage({ type: 'error', text: result.error || 'Failed to delete API key' });
    }

    setDeletingKey(null);
  };

  if (!user) return null;

  const linkedProviders = getLinkedProviders();
  const hasGoogle = linkedProviders.includes('google.com');

  return (
    <div className="glass rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Account Settings</h3>

      <div className="space-y-4">
        {/* User Info */}
        <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
          {user.photoURL && (
            <img src={user.photoURL} alt="" className="w-12 h-12 rounded-full" />
          )}
          <div>
            <div className="text-white font-medium">{user.name}</div>
            <div className="text-sm text-gray-400">{user.email}</div>
          </div>
        </div>

        {/* Linked Providers */}
        <div>
          <h4 className="text-sm font-medium text-gray-400 mb-3">Linked Accounts</h4>
          <div className="space-y-2">
            {/* Google */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              hasGoogle ? 'bg-success-500/10 border border-success-500/20' : 'glass'
            }`}>
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-white">Google</span>
              </div>
              {hasGoogle ? (
                <span className="text-sm text-success-400">Connected</span>
              ) : (
                <span className="text-sm text-gray-500">Not connected</span>
              )}
            </div>

            {/* GitHub App */}
            <div className="flex items-center justify-between p-3 rounded-lg glass">
              <div className="flex items-center gap-3">
                <Github className="h-5 w-5 text-white" />
                <span className="text-white">GitHub App</span>
              </div>
              <button
                onClick={connectGitHub}
                className="text-sm px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors flex items-center gap-1"
              >
                Manage
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>

        {/* API Keys Section — superadmin only (platform keys from GCP Secret Manager) */}
        {isSuperadmin() && <div className="pt-4 border-t border-white/10">
          <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <Key className="h-4 w-4" />
            API Keys
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            Configure API keys for advanced features like sandboxed code execution and AI-powered analysis.
          </p>

          {/* AI Configuration Cross-link */}
          {onNavigateToAIConfig && (
            <div className="bg-secondary-500/10 border border-secondary-500/30 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-3">
                <Brain className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                <div>
                  <p className="text-secondary-300 text-sm font-medium">LLM Provider Keys</p>
                  <p className="text-gray-400 text-xs">
                    Configure your own LLM provider API keys in{' '}
                    <button
                      onClick={onNavigateToAIConfig}
                      className="text-secondary-400 hover:text-secondary-300 underline underline-offset-2"
                    >
                      AI Configuration
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {Object.values(API_KEY_TYPES).map((keyConfig) => {
              const keyId = keyConfig.id;
              const keyStatus = apiKeys[keyId] || { configured: false, masked: null };
              const isConfigured = keyStatus.configured;

              return (
                <div key={keyId} className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="text-white font-medium flex items-center gap-2">
                        {keyConfig.name}
                        {isConfigured && (
                          <span className="text-xs text-success-400 bg-success-500/20 px-2 py-0.5 rounded">
                            Configured
                          </span>
                        )}
                      </h5>
                      <p className="text-xs text-gray-400 mt-1">{keyConfig.description}</p>
                    </div>
                    <a
                      href={keyConfig.docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                      title="View documentation"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>

                  {isConfigured ? (
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex-1 px-3 py-2 bg-black/30 rounded text-sm text-gray-400 font-mono">
                        {keyStatus.masked || '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                      </div>
                      <button
                        onClick={() => handleTestApiKey(keyId)}
                        disabled={testingKey === keyId}
                        className="px-3 py-2 bg-secondary-600 hover:bg-secondary-500 text-white text-sm rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                        title="Test API key"
                      >
                        {testingKey === keyId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        Test
                      </button>
                      <button
                        onClick={() => handleDeleteApiKey(keyId)}
                        disabled={deletingKey === keyId}
                        className="px-3 py-2 bg-error-600/80 hover:bg-error-500 text-white text-sm rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                        title="Delete API key"
                      >
                        {deletingKey === keyId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-3">
                      <div className="relative flex-1">
                        <input
                          type={keyVisibility[keyId] ? 'text' : 'password'}
                          value={keyInputs[keyId] || ''}
                          onChange={(e) => setKeyInputs(prev => ({ ...prev, [keyId]: e.target.value }))}
                          placeholder={keyConfig.placeholder}
                          autoComplete="off"
                          data-1p-ignore="true"
                          data-lpignore="true"
                          className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm font-mono placeholder-gray-500 focus:outline-none focus:border-primary-500"
                        />
                        <button
                          type="button"
                          onClick={() => setKeyVisibility(prev => ({ ...prev, [keyId]: !prev[keyId] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {keyVisibility[keyId] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <button
                        onClick={() => handleSaveApiKey(keyId)}
                        disabled={savingKey === keyId || !keyInputs[keyId]?.trim()}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white text-sm rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {savingKey === keyId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Save'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* API Key Message */}
          {keyMessage && (
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              keyMessage.type === 'success'
                ? 'bg-success-500/20 border border-success-500/30 text-success-300'
                : 'bg-error-500/20 border border-error-500/30 text-error-300'
            }`}>
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{keyMessage.text}</span>
            </div>
          )}
        </div>}

        {/* Status Message */}
        {message && (
          <div className={`p-3 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-success-500/20 border border-success-500/30 text-success-300'
              : 'bg-error-500/20 border border-error-500/30 text-error-300'
          }`}>
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Appearance */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
            <span aria-hidden="true">Aa</span>
            Readability
          </h3>
          <TextSizeControl variant="full" />
        </div>

        {/* Help & Onboarding */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-gray-400" aria-hidden="true" />
            Help &amp; Onboarding
          </h3>
          <button
            onClick={() => { resetPostImportTour(); window.location.reload(); }}
            className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
          >
            Restart post-import tour
          </button>
        </div>
      </div>
    </div>
  );
};
